import React, { useEffect, useState, useCallback } from "react";
import api from "../common/apiClient";
import SummaryApi from "../common/SummaryApi";
import { getLocalCart, updateLocalCart, removeFromLocalCart } from "../helpers/cartHelper";
import { normalizeCartItem } from "../helpers/cartNormalizer.js";
import { useCart } from "../context/CartContext";
import { Trash2, Truck, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FiMinus, FiPlus } from "react-icons/fi";
import { LuShoppingBag } from "react-icons/lu";
import Breadcrumb from "../components/BreadCrumb.js";
import DeleteConfirmModal from "../helpers/DeleteConfirmModal.js";
import LoginRedirectModal from "../helpers/LoginRedirectModal.js";
import toast from "react-hot-toast";

const SkeletonBox = ({ className = "" }) => (
  <div
    className={`
      relative overflow-hidden
      bg-gray-200 rounded
      ${className}
    `}
  >
    {/* Shimmer */}
    <span
      className="
        absolute inset-0
        -translate-x-full
        animate-[shimmer_1.4s_infinite]
        bg-gradient-to-r
        from-transparent
        via-white/40
        to-transparent
      "
    />
  </div>
);

const CartSkeleton = () => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

      {/* ================= LEFT CART ================= */}
      <div className="lg:col-span-2 space-y-6">

        <div className="bg-white rounded-xl border border-[#B5CAFF] p-5 space-y-6">

          {/* Header */}
          <div className="flex items-center gap-3">
            <SkeletonBox className="w-4 h-4" />
            <SkeletonBox className="w-40 h-4" />
          </div>

          {[1, 2].map((_, i) => (
            <div
              key={i}
              className="py-5 border-b last:border-none"
            >

              {/* ================= MOBILE SKELETON ================= */}
              <div className="md:hidden space-y-4">

                <div className="flex gap-3">
                  <SkeletonBox className="w-5 h-5 mt-1" />
                  <SkeletonBox className="w-[90px] h-[90px] rounded-md" />
                  <SkeletonBox className="flex-1 h-4" />
                </div>

                <div className="flex justify-between">
                  <SkeletonBox className="w-20 h-3" />
                  <SkeletonBox className="w-24 h-3" />
                </div>

                <div className="flex justify-between items-center">
                  <SkeletonBox className="w-[90px] h-[36px] rounded-md" />
                  <SkeletonBox className="w-16 h-3" />
                </div>
              </div>

              {/* ================= DESKTOP SKELETON ================= */}
              <div className="hidden md:flex gap-6">

                <div className="flex items-start gap-4">
                  <SkeletonBox className="w-5 h-5 mt-2" />
                  <SkeletonBox className="w-[110px] h-[110px] rounded-md" />
                </div>

                <div className="flex-1 space-y-3">
                  <SkeletonBox className="w-3/4 h-4" />
                  <SkeletonBox className="w-24 h-3" />
                  <SkeletonBox className="w-32 h-3" />
                  <SkeletonBox className="w-[90px] h-[36px] rounded-md mt-2" />
                </div>

                <div className="flex flex-col items-end gap-2">
                  <SkeletonBox className="w-16 h-4" />
                  <SkeletonBox className="w-12 h-3" />
                  <SkeletonBox className="w-20 h-3" />
                </div>
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* ================= RIGHT SUMMARY ================= */}
      <div className="space-y-6">

        <div className="bg-white rounded-xl border border-[#B5CAFF] p-5 space-y-5">
          <SkeletonBox className="w-32 h-4" />

          {[1, 2, 3].map((_, i) => (
            <div key={i} className="flex justify-between">
              <SkeletonBox className="w-24 h-3" />
              <SkeletonBox className="w-16 h-3" />
            </div>
          ))}

          <SkeletonBox className="w-full h-11 rounded-md mt-4" />
        </div>

        <div className="bg-white rounded-xl border border-[#B5CAFF] p-5 space-y-3">
          <SkeletonBox className="w-40 h-3" />
          <SkeletonBox className="w-32 h-3" />
        </div>
      </div>
    </div>
  );
};

const EmptyCart = () => {
  const navigate = useNavigate();

  return (
    <div className=" shadow-[0_2px_8px_rgba(0,0,0,0.03),0_8px_24px_rgba(0,0,0,0.03)] rounded-2xl p-6 sm:p-10">

      <div className="flex flex-col items-center text-center">

        {/* ILLUSTRATION */}
        <div className="relative mb-6">
          <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-[#F2F6FF] flex items-center justify-center">
            <LuShoppingBag className="w-14 h-14 text-[#007BFF]" />
          </div>

          {/* subtle dot */}
          <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
        </div>

        {/* TITLE */}
        <h2 className="text-xl sm:text-2xl font-semibold text-[#191C1F] mb-2">
          Your shopping cart is empty
        </h2>

        {/* SUBTEXT */}
        <p className="text-sm sm:text-base text-[#5F6C72] max-w-lg mb-6">
          Add items you love to your cart.  
          Enjoy secure checkout, fast delivery, and easy returns.
        </p>

        {/* ACTION BUTTON */}
        <button
          onClick={() => navigate("/all-userproducts")}
          className="
            w-full sm:w-auto
            bg-[#007BFF] hover:bg-blue-700
            text-white font-medium
            px-10 py-3
            rounded-lg
            transition
            shadow-sm hover:shadow-md
          "
        >
          Start Shopping
        </button>

        {/* TRUST POINTS */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-3xl text-sm text-[#5F6C72]">

          <div className="flex items-center justify-center gap-2">
            <Truck size={16} className="text-[#007BFF]" />
            Fast Delivery
          </div>

          <div className="flex items-center justify-center gap-2">
            <ShieldCheck size={16} className="text-[#007BFF]" />
            Secure Payments
          </div>

          <div className="flex items-center justify-center gap-2">
            <Trash2 size={16} className="text-[#007BFF]" />
            Easy Returns
          </div>

        </div>
      </div>
    </div>
  );
};

const CartPage = () => {
  const token = localStorage.getItem("token");
  const { refreshCart } = useCart();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [redirecting, setRedirecting] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const countdownRef = React.useRef(null);

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "Products", path: "/all-userproducts" },
    { label: "Shopping Cart" },
  ];


  const openRemoveModal = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    if (cartItems.length > 0) {
      setSelectedIds(cartItems.map(item => item._id));
    }
  }, [cartItems]);

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  /* ================= LOAD CART ================= */
  const loadCart = useCallback(async () => {
    try {
      if (token) {
        // LOGGED IN
        const res = await api({
          url: SummaryApi.getCartItems.url,
          method: SummaryApi.getCartItems.method
        });

        setCartItems(res.data.items.map(normalizeCartItem));
      } else {
        // 🟡 GUEST USER
        const guestCart = getLocalCart();

        if (guestCart.length === 0) {
          setCartItems([]);
          return;
        }

        const res = await api({
          url: SummaryApi.getGuestCart.url, // 👈 NEW API
          method: "POST",
          data: { items: guestCart }
        });

        setCartItems(res.data.items.map(normalizeCartItem));
      }
    } catch (err) {
      console.error("Cart load failed", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  /* ================= QTY UPDATE ================= */
  const updateQty = async (item, type) => {
    const newQty = type === "inc"
      ? item.quantity + 1
      : item.quantity - 1;

    if (newQty < 1) return;

    try {
      if (token) {
        await api({
          url: SummaryApi.updateCartItem.url(item._id),
          method: SummaryApi.updateCartItem.method,
          data: { quantity: newQty }
        });
      } else {
        updateLocalCart(item._id, newQty);
      }

      // ✅ success → reload cart
      loadCart();

    } catch (err) {
      console.error("Update qty failed:", err);

      // ✅ BACKEND ERROR MESSAGE SHOW
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Unable to update quantity";

      toast.error(message);

      // 🔁 reload cart to reset wrong UI state
      loadCart();
    }
  };

  /* ================= REMOVE ITEM ================= */
  const confirmRemoveItem = async () => {
    if (!selectedItem) return;

    try {
      setDeleteLoading(true);

      if (token) {
        await api({
          url: SummaryApi.removeCartItem.url(selectedItem._id),
          method: SummaryApi.removeCartItem.method
        });
      } else {
        removeFromLocalCart(selectedItem._id);
      }

      refreshCart();
      loadCart();
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (err) {
      console.error("Remove failed", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const proceedToCheckout = () => {
    if (selectedItems.length === 0) return;

    if (!token) {
      setShowLoginModal(true);
      setRedirecting(true);
      setCountdown(10);

      // start countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);

      return;
    }

    navigate("/check-out-page", {
      state: {
        items: selectedItems,
        from: "cart",
        summary: {
          subtotal: summary.subtotal,
          discount: summary.discount,
          payable: summary.payable,
        },
      },
    });
  };


  useEffect(() => {
    if (!showLoginModal) return;

    if (countdown === 0) {
      clearInterval(countdownRef.current);
      navigate("/login", {
        state: { redirectTo: "/cart-page" },
      });
    }
  }, [countdown, showLoginModal, navigate]);

  const cancelLoginRedirect = () => {
    clearInterval(countdownRef.current);
    setShowLoginModal(false);
    setRedirecting(false);
  };

  const proceedLoginNow = () => {
    clearInterval(countdownRef.current);
    navigate("/login", {
      state: { redirectTo: "/cart-page" },
    });
  };


  const selectedItems = cartItems.filter(item =>
    selectedIds.includes(item._id)
  );

  const totalQuantity = selectedItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const summary = selectedItems.reduce(
    (acc, item) => {
      const mrpTotal = item.mrp * item.quantity;
      const priceTotal = item.price * item.quantity;

      acc.subtotal += mrpTotal;
      acc.discount += mrpTotal - priceTotal;
      acc.payable += priceTotal;

      return acc;
    },
    { subtotal: 0, discount: 0, payable: 0 }
  );

  const DELIVERY_FREE_LIMIT = 1000;

  const isFreeDelivery =
    summary.payable >= DELIVERY_FREE_LIMIT || summary.payable === 0;

  const grandTotal = summary.payable;

  return (
    <div className="py-4">
      <div className="mx-auto px-4">

        {/* PAGE TITLE */}
        <h1 className="text-xl hidden md:block font-semibold mb-2">Shopping Cart</h1>

        {/* BREADCRUMB */}
        <div className="mb-4">
          <Breadcrumb items={breadcrumbItems} />
        </div>

      {/*  CONTENT AREA */}
      {loading ? (
        <CartSkeleton />
      ) : cartItems.length === 0 ? (
        <EmptyCart />
      ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* LEFT SECTION */}
            <div className="lg:col-span-2 space-y-6">

              {/* CART BOX */}
              <div className="bg-white rounded-xl border border-[#B5CAFF] p-4">
                {cartItems.length > 0 && (
                  <div className="flex items-center mb-4">
                    <LuShoppingBag className="w-4 h-4 inline mr-1" />
                    <h2 className="text-sm font-semibold">
                      SHOPPING CART ({cartItems.length} ITEM{cartItems.length > 1 ? "S" : ""})
                    </h2>
                  </div>
                )}
                {/* CART ITEM */}
                {cartItems.map((item, index) => (
                  <div
                    key={item._id}
                    className={`pt-8 ${index !== cartItems.length - 1 ? "border-b pb-8 border-gray-200" : ""
                      }`}
                  >
                    {/* ================= MOBILE VIEW ================= */}
                    <div className="md:hidden">
                      {/* TOP ROW */}
                      <div className="flex gap-2">
                        {/* CHECKBOX */}
                        <button
                          onClick={() => toggleSelect(item._id)}
                          className={`
                          w-4 h-4 mt-1 rounded border flex items-center justify-center
                          ${selectedIds.includes(item._id)
                              ? "bg-[#007BFF] border-[#007BFF]"
                              : "bg-white border-gray-300"}
                        `}
                        >
                          {selectedIds.includes(item._id) && (
                            <svg width="12" height="12" viewBox="0 0 24 24" stroke="white" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </button>

                        {/* IMAGE */}
                        <img
                          src={item.image}
                          alt={item.productName}
                          className="w-[90px] h-[90px] object-fill rounded-md"
                        />

                        {/* NAME */}
                        <div className="flex-1">
                          <p
                            title={item.productName}
                            className="text-sm font-medium leading-snug line-clamp-2"
                          >
                            {item.productName}
                          </p>

                        {/* Stock Status */}
                        {item.stock === 0 ? (
                          <p className="text-[9px] font-bold uppercase mb-3 text-red-500">
                            Out Of Stock
                          </p>
                        ) : item.stock <= 5 ? (
                          <p className="text-[9px] font-bold uppercase mb-3 text-orange-500 animate-pulse">
                            🔥 Only {item.stock} left - Hurry Up!
                          </p>
                        ) : item.stock <= 10 ? (
                          <p className="text-[9px] font-bold uppercase mb-3 text-yellow-600">
                            ⚡ Only Few Left
                          </p>
                        ) : null}
                        </div>
                      </div>

                      {/* SIZE + PRICE */}
                      <div className="mt-3 flex justify-between text-xs font-medium">
                        <span>
                          {item.size && <>Size: <span className="text-[#5F6C72]">{item.size}</span></>}
                        </span>

                        <div className="text-right flex flex-row justify-center items-center">
                          <p className="font-semibold text-black text-sm mr-2">₹{item.price}</p>
                          <p className="line-through text-gray-400 font-normal">₹{item.mrp}</p>
                        </div>
                      </div>

                      {/* COLOR + OFF */}
                      <div className="mt-1 flex justify-between items-center text-xs">
                        <div className="flex items-center gap-2">
                          {item.color && (
                            <>
                              <span className="font-medium">Color:</span>
                              <span
                                className="w-6 h-3 rounded-sm border"
                                style={{ backgroundColor: item.colorCode || item.color }}
                              />
                            </>
                          )}
                        </div>

                        {item.discountPercent > 0 && (
                          <span className="bg-yellow-300 text-[11px] px-2 py-[3px] rounded-sm font-semibold text-black">
                            {item.discountPercent}% OFF
                          </span>
                        )}
                      </div>

                      {/* QTY + REMOVE */}
                      <div className="mt-4 flex justify-between items-center">
                        {/* QTY */}
                        <div className="flex items-center justify-between w-[80px] h-[36px] px-2 border-2 border-[#D9F2F9] bg-[#F6FAFD] rounded-lg">
                          <button
                            onClick={() => updateQty(item, "dec")}
                            disabled={item.quantity === 1}
                            className={item.quantity === 1
                              ? "text-gray-300 cursor-not-allowed"
                              : "text-[#007BFF] cursor-pointer"}
                          >
                            <FiMinus className="w-4 h-4" />
                          </button>

                          <span className="text-sm font-medium">{item.quantity}</span>

                          <button
                            onClick={() => updateQty(item, "inc")}
                            className="text-[#007BFF] cursor-pointer"
                          >
                            <FiPlus className="w-4 h-4" />
                          </button>
                        </div>

                        {/* REMOVE */}
                        <button
                          onClick={() => openRemoveModal(item)}
                          className="flex items-center gap-1 text-sm text-[#FC0924]"
                        >
                          <Trash2 size={16} /> Remove
                        </button>
                      </div>
                    </div>

                    {/* ================= DESKTOP / TAB VIEW ================= */}
                    <div className="hidden md:flex md:flex-row gap-10">
                      <div className="flex items-center gap-4">
                        {/* CHECKBOX */}
                        <button
                          onClick={() => toggleSelect(item._id)}
                          className={`
                                    w-5 h-5 rounded-md border flex items-center justify-center mt-1
                                    ${selectedIds.includes(item._id)
                              ? "bg-[#007BFF] border-[#007BFF]"
                              : "bg-white border-gray-300"}
                                  `}
                        >
                          {selectedIds.includes(item._id) && (
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="3"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </button>

                        {/* IMAGE */}
                        <img
                          src={item.image}
                          alt=""
                          className="w-[114px] h-[125px] object-fill rounded-md"
                        />
                      </div>

                      {/* DETAILS */}
                      <div className="flex-1">
                        <h1
                          title={item.productName}
                          className="text-sm font-medium leading-snug line-clamp-2"
                        >
                          {item.productName}
                        </h1>

                        {/* Stock Status */}
                        {item.stock === 0 ? (
                          <p className="text-[11px] font-bold uppercase mb-3 text-red-500">
                            Out Of Stock
                          </p>
                        ) : item.stock <= 5 ? (
                          <p className="text-[11px] font-bold uppercase mb-3 text-orange-500 animate-pulse">
                            🔥 Only {item.stock} left - Hurry Up!
                          </p>
                        ) : item.stock <= 10 ? (
                          <p className="text-[11px] font-bold uppercase mb-3 text-yellow-600">
                            ⚡ Only Few Left
                          </p>
                        ) : null}

                        {item.size && (
                          <p className="text-xs font-medium mt-1">
                            Size: <span className="text-[#5F6C72]">{item.size}</span>
                          </p>
                        )}

                        {item.color && (
                          <div className="flex items-center gap-1 text-xs mt-1">
                            <span className="font-medium">Color:</span>

                            {/* Color dot */}
                            <span
                              className="w-7 h-3 rounded-sm "
                              style={{ backgroundColor: item.colorCode || item.color }}
                            />

                            {/* <span className="font-medium">{item.color}</span> */}
                          </div>
                        )}


                        {/* QTY */}
                        <div className="mt-4">
                          <div
                            className="
                                        flex items-center justify-between
                                        w-[80px] h-[36px]
                                        px-2
                                        border-2 border-[#D9F2F9]
                                        rounded-md
                                        bg-white
                                      "
                          >
                            <button
                              onClick={() => updateQty(item, "dec")}
                              disabled={item.quantity === 1}
                              className={`text-lg font-semibold ${item.quantity === 1
                                  ? "text-gray-300 cursor-not-allowed"
                                  : "text-[#007BFF]"
                                }`}
                            >
                              <FiMinus className="w-4 h-4" />
                            </button>

                            <span className="text-sm font-medium text-center">
                              {item.quantity}
                            </span>

                            <button
                              onClick={() => updateQty(item, "inc")}
                              className="text-[#007BFF] text-lg font-semibold leading-none"
                            >
                              <FiPlus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* PRICE + ACTIONS */}
                      <div
                        className="
                                    flex md:flex-col justify-between
                                    items-center md:items-end
                                    gap-3 md:gap-1
                                    pt-4 md:pt-0
                                  "
                      >
                        <div className="text-left md:text-right">
                          <p className="font-semibold">₹{item.price}</p>
                          <p className="text-xs line-through text-[#5F6C72]">₹{item.mrp}</p>

                          {item.discountPercent > 0 && (
                            <span className="inline-block mt-1 bg-yellow-300 text-[11px] px-2 py-[3px] rounded-sm font-semibold text-black">
                              {item.discountPercent}% OFF
                            </span>
                          )}
                        </div>

                        <button
                          onClick={() => openRemoveModal(item)}
                          className="flex items-center gap-1 text-sm text-[#FC0924]"
                        >
                          <Trash2 size={16} /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT SECTION */}
            <div className="space-y-6">

              {/* ORDER SUMMARY */}
              <div className="bg-white rounded-xl border border-[#B5CAFF] p-4">
                <h2 className="text-base font-medium mb-4">Order Summary</h2>

                <div className="space-y-5 text-sm font-medium text-[#5F6C72]">
                  <div className="flex justify-between">
                    <span>
                      Subtotal ({selectedItems.length} item{selectedItems.length > 1 ? "s" : ""})
                    </span>
                    <span>₹{summary.subtotal.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Total Quantity</span>
                    <span>{totalQuantity}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Discount on MRP</span>
                    <span>-₹{summary.discount.toFixed(2)}</span>
                  </div>

                  {summary.payable > 0 && (
                    <div className="flex justify-between items-center">
                      <span>Delivery</span>

                      {isFreeDelivery ? (
                        <span className="text-green-600 font-medium">
                          Free
                        </span>
                      ) : (
                        <span className="text-orange-600 font-medium">
                          Charges applicable
                        </span>
                      )}
                    </div>
                  )}
                  <hr />
                  <div className="flex justify-between font-semibold text-black text-base">
                    <span>Total</span>
                    <span>₹{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex justify-center mt-3">
                <button
                  disabled={selectedItems.length === 0 || redirecting}
                  onClick={proceedToCheckout}
                  className={`
                  w-full md:w-auto px-8 py-3 rounded-md mt-5 font-medium mb-2
                  ${selectedItems.length === 0 || redirecting
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-[#007BFF] hover:bg-blue-700 text-white"}
                `}
                >
                  {redirecting ? "Redirecting to login..." : "PROCEED TO CHECKOUT"}
                </button>
                </div>
                {/* SAVINGS INFO */}
                {summary.discount > 0 && (
                  <p className="mt-4 py-3 mb-4 rounded-lg text-sm bg-green-100 text-[#008622] text-center">
                    You will save ₹{summary.discount.toFixed(2)} on this order
                  </p>
                )}
              </div>

              {/* Info Cards */}
              <div className="bg-white rounded-xl border border-[#B5CAFF] p-6 space-y-5 text-gray-500">
                  <div className="flex items-center gap-4"><Truck size={20}/><div><p className="text-[13px] font-bold text-black">Free Shipping</p><p className="text-[11px]">On orders over ₹1000</p></div></div>
                  <div className="flex items-center gap-4 border-t pt-5 border-gray-50"><ShieldCheck size={20}/><div><p className="text-[13px] font-bold text-black">Secure Packaging</p><p className="text-[11px]">Art safely packed & insured</p></div></div>
              </div>
            </div>
          </div>
        )}
      </div>
      <DeleteConfirmModal
        open={showDeleteModal}
        title="Remove item"
        description="Are you sure you want to remove this item from your cart?"
        loading={deleteLoading}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={confirmRemoveItem}
      />
      <LoginRedirectModal
        open={showLoginModal}
        seconds={countdown}
        onCancel={cancelLoginRedirect}
        onProceed={proceedLoginNow}
      />
    </div>
  );
};

export default CartPage;
